import {
  exact,
  fallback,
  readString,
  segment,
  type V2ConsoleTarget
} from '@/utils/v2-console-target/shared'

const pulsarClusterTabs: Record<string, string> = {
  ClusterPage: 'overview',
  ClusterDashboard: 'overview',
  ClusterOverview: 'details',
  ClusterStorageOverview: 'storage',
  ClusterAuditLog: 'audit',
  MetricsApiPage: 'metrics-api',
  LakehouseTableOverviewPage: 'lakehouse-table'
}

const pulsarTopicTabs: Record<string, string> = {
  TopicPage: 'overview',
  TopicMetrics: 'overview',
  TopicOverview: 'stats',
  TopicStats: 'stats',
  TopicSchema: 'schema',
  TopicMessages: 'messages',
  TopicConfiguration: 'policies',
  TopicLakehouseTable: 'lakehouse'
}

const unsupportedPulsarClusterRoutes = new Set([
  'PfSqlListPage',
  'PfSqlDeployPage',
  'PfSqlPage',
  'PfSqlOverview',
  'PfSqlLogs',
  'PfSqlExceptions'
])

const pulsarRoutes = new Set([
  ...Object.keys(pulsarClusterTabs),
  ...Object.keys(pulsarTopicTabs),
  ...unsupportedPulsarClusterRoutes,
  'ClusterConfiguration',
  'PulsarClientsPage',
  'KafkaClientsPage',
  'MqttClientsPage',
  'LakehouseTableConfigurationPage',
  'ConsumerGroupListPage',
  'ConsumerGroupPage',
  'TenantsPage',
  'TenantDashboard',
  'TenantConfiguration',
  'NamespacesPage',
  'NamespaceDashboard',
  'NamespaceOverview',
  'NamespaceConfiguration',
  'TopicsPage',
  'TopicLakehouseTableConfiguration',
  'SubjectSchemaRegistry',
  'TopicClients',
  'AgentsPage',
  'AgentPage',
  'AgentDashboard',
  'AgentOverview',
  'AgentLogs',
  'AgentTools',
  'PulsarConnectorsPage',
  'PulsarConnectorCreatePage',
  'PulsarConnectorEditPage',
  'PulsarConnectorPage',
  'PulsarConnectorDashboard',
  'PulsarConnectorLogs',
  'PulsarConnectorExceptions',
  'ConnectorsPage',
  'KafkaConnectorsPage',
  'KafkaConnectorCreatePage',
  'KafkaConnectorEditPage',
  'KafkaConnectorPage',
  'KafkaConnectorDashboard',
  'KafkaConnectorLogs',
  'KafkaConnectorConfigurationPage',
  'FunctionsPage',
  'FunctionPage',
  'FunctionOverview',
  'FunctionDashboard',
  'FunctionLogs',
  'FunctionExceptions'
])

type PulsarTargetContext = {
  routeName: string
  params?: Record<string, unknown>
  pulsarClusterBase?: string
  instanceFallback: V2ConsoleTarget
  tenant?: string
  namespace?: string
  topic?: string
  topicPersistent?: boolean
  consumerGroupId?: string
}

export function resolvePulsarTarget({
  routeName,
  params,
  pulsarClusterBase,
  instanceFallback,
  tenant,
  namespace,
  topic,
  topicPersistent,
  consumerGroupId
}: PulsarTargetContext): V2ConsoleTarget | undefined {
  if (!pulsarClusterBase) {
    return pulsarRoutes.has(routeName) ? instanceFallback : undefined
  }

  const clusterFallback = fallback(`${pulsarClusterBase}/overview`, 'pulsarCluster')
  const clusterTab = pulsarClusterTabs[routeName]
  if (clusterTab !== undefined) return exact(`${pulsarClusterBase}/${clusterTab}`)

  if (routeName === 'ClusterConfiguration') {
    return fallback(`${pulsarClusterBase}/details`, 'pulsarClusterDetails')
  }
  if (routeName === 'PulsarClientsPage') return exact(`${pulsarClusterBase}/clients/pulsar`)
  if (routeName === 'KafkaClientsPage') return exact(`${pulsarClusterBase}/clients/kafka`)
  if (routeName === 'MqttClientsPage') return exact(`${pulsarClusterBase}/clients/mqtt`)
  if (routeName === 'LakehouseTableConfigurationPage') {
    return exact(`${pulsarClusterBase}/lakehouse-table/configure`)
  }

  const topicBase =
    tenant && namespace && topic && topicPersistent !== undefined
      ? `${pulsarClusterBase}/tenants/${segment(tenant)}/namespaces/${segment(namespace)}/topics/${
          topicPersistent ? 'persistent' : 'non-persistent'
        }/${segment(topic)}`
      : undefined

  if (routeName === 'ConsumerGroupListPage') {
    return exact(`${pulsarClusterBase}/consumer-groups`)
  }
  if (routeName === 'ConsumerGroupPage') {
    if (!consumerGroupId) return clusterFallback
    return topicBase
      ? exact(`${topicBase}/consumer-groups/${segment(consumerGroupId)}`)
      : exact(`${pulsarClusterBase}/consumer-groups/${segment(consumerGroupId)}`)
  }

  if (routeName === 'TenantsPage') return exact(`${pulsarClusterBase}/tenants`)

  const tenantBase = tenant ? `${pulsarClusterBase}/tenants/${segment(tenant)}` : undefined
  const tenantFallback = tenantBase ? fallback(`${tenantBase}/overview`, 'tenant') : clusterFallback

  if (routeName === 'TenantDashboard') {
    return tenantBase ? exact(`${tenantBase}/overview`) : clusterFallback
  }
  if (routeName === 'TenantConfiguration') {
    return tenantBase ? exact(`${tenantBase}/details`) : clusterFallback
  }
  if (routeName === 'NamespacesPage') {
    return tenantBase ? exact(`${tenantBase}/namespaces`) : clusterFallback
  }

  const namespaceBase =
    tenantBase && namespace ? `${tenantBase}/namespaces/${segment(namespace)}` : undefined
  const namespaceFallback = namespaceBase
    ? fallback(`${namespaceBase}/overview`, 'namespace')
    : tenantFallback

  if (routeName === 'NamespaceDashboard' || routeName === 'NamespaceOverview') {
    return namespaceBase ? exact(`${namespaceBase}/overview`) : tenantFallback
  }
  if (routeName === 'NamespaceConfiguration') {
    return namespaceBase ? exact(`${namespaceBase}/policies`) : tenantFallback
  }
  if (routeName === 'TopicsPage') {
    return namespaceBase ? exact(`${namespaceBase}/topics`) : tenantFallback
  }

  const topicFallback = topicBase ? fallback(`${topicBase}/overview`, 'topic') : namespaceFallback
  const topicTab = pulsarTopicTabs[routeName]
  if (topicTab !== undefined) {
    return topicBase ? exact(`${topicBase}/${topicTab}`) : namespaceFallback
  }
  if (routeName === 'TopicLakehouseTableConfiguration') {
    return topicBase ? exact(`${topicBase}/configure-lakehouse`) : namespaceFallback
  }
  if (routeName === 'SubjectSchemaRegistry') {
    return topicBase ? exact(`${topicBase}/schema`) : namespaceFallback
  }
  if (routeName === 'TopicClients') return topicFallback

  if (
    routeName === 'AgentsPage' ||
    routeName === 'AgentPage' ||
    routeName === 'AgentDashboard' ||
    routeName === 'AgentOverview' ||
    routeName === 'AgentLogs' ||
    routeName === 'AgentTools'
  ) {
    return namespaceFallback
  }

  const name = readString(params, 'name')
  const pulsarConnectorBase = namespaceBase ? `${namespaceBase}/pulsar-connectors` : undefined
  if (routeName === 'PulsarConnectorsPage') {
    return pulsarConnectorBase ? exact(pulsarConnectorBase) : namespaceFallback
  }
  if (routeName === 'PulsarConnectorCreatePage') {
    return pulsarConnectorBase ? exact(`${pulsarConnectorBase}/new`) : namespaceFallback
  }
  if (routeName === 'PulsarConnectorEditPage') {
    return pulsarConnectorBase && name
      ? exact(`${pulsarConnectorBase}/${segment(name)}/edit`)
      : namespaceFallback
  }
  if (routeName === 'PulsarConnectorPage' || routeName === 'PulsarConnectorDashboard') {
    return pulsarConnectorBase && name
      ? exact(`${pulsarConnectorBase}/${segment(name)}`)
      : namespaceFallback
  }
  if (routeName === 'PulsarConnectorLogs' || routeName === 'PulsarConnectorExceptions') {
    return pulsarConnectorBase && name
      ? fallback(`${pulsarConnectorBase}/${segment(name)}`, 'connector')
      : namespaceFallback
  }

  const kafkaConnectorBase = `${pulsarClusterBase}/kafka-connectors`
  if (routeName === 'ConnectorsPage') return clusterFallback
  if (routeName === 'KafkaConnectorsPage') return exact(kafkaConnectorBase)
  if (routeName === 'KafkaConnectorCreatePage') return exact(`${kafkaConnectorBase}/new`)
  if (routeName === 'KafkaConnectorEditPage') {
    return name ? exact(`${kafkaConnectorBase}/${segment(name)}/edit`) : clusterFallback
  }
  if (routeName === 'KafkaConnectorPage' || routeName === 'KafkaConnectorDashboard') {
    return name ? exact(`${kafkaConnectorBase}/${segment(name)}`) : clusterFallback
  }
  if (routeName === 'KafkaConnectorLogs' || routeName === 'KafkaConnectorConfigurationPage') {
    return name ? fallback(`${kafkaConnectorBase}/${segment(name)}`, 'connector') : clusterFallback
  }

  const functionBase = namespaceBase ? `${namespaceBase}/functions` : undefined
  if (routeName === 'FunctionsPage') {
    return functionBase ? exact(functionBase) : clusterFallback
  }
  if (
    routeName === 'FunctionPage' ||
    routeName === 'FunctionOverview' ||
    routeName === 'FunctionDashboard'
  ) {
    return functionBase && name ? exact(`${functionBase}/${segment(name)}`) : namespaceFallback
  }
  if (routeName === 'FunctionLogs' || routeName === 'FunctionExceptions') {
    return functionBase && name
      ? fallback(`${functionBase}/${segment(name)}`, 'function')
      : namespaceFallback
  }

  if (unsupportedPulsarClusterRoutes.has(routeName)) return clusterFallback

  return undefined
}
